/**
 * ask_user question card (PlayAd "1 of 2" card): one step per question — tall image tiles in a carousel,
 * chips / rows for text options, an "Other" box, free text, or an upload step ("Selected" tiles + "+" →
 * Upload / Asset Library / Creations, with Skip). Cancel / Next → Submit.
 * Submit → POST /api/chat/threads/:id/answers { attachmentId, answers:[{questionId, optionIds?, text?, files?, skipped?}] }.
 */
import { Check, ChevronDown, ChevronLeft, ChevronRight, CircleHelp, Film, GalleryVerticalEnd, Image as ImageIcon, Maximize2, Plus, Upload, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { AnswerFile, AnswerItem } from "@/api/chat";
import { errorMessage, uploadFile } from "@/api/client";
import { toast } from "@/api/toast-store";
import { cn } from "@/lib/cn";
import { AssetPicker } from "../assets/AssetPicker";
import { Pill } from "../ui/Badge";
import { Button } from "../ui/Button";
import { Spinner } from "../ui/Feedback";
import { Lightbox } from "../ui/Lightbox";
import { PopoverPanel, usePopover } from "../ui/Overlay";
import { LibraryPicker } from "./LibraryPicker";
import { isVideoUrl, obj, str } from "./util";
import { safeMediaHref, safePreviewSrc } from "@/lib/safe-url";

export interface QOption {
  id: string;
  label: string;
  imageUrl?: string;
  description?: string;
}
export interface QItem {
  id: string;
  prompt: string;
  options: QOption[];
  allowOther: boolean;
  multi: boolean;
  image: boolean;
  text: boolean;
  upload: boolean;
  accept: ("image" | "video")[];
  minFiles: number;
  maxFiles: number;
}

export function normalizeQuestions(data: Record<string, unknown> | undefined): { title?: string; questions: QItem[] } {
  const d = data ?? {};
  const raw = Array.isArray(d.questions) ? d.questions : Array.isArray(obj(d.input)?.questions) ? (obj(d.input)!.questions as unknown[]) : [];
  const questions = raw
    .map((q, i): QItem | null => {
      const o = obj(q);
      if (!o) return null;
      const options = (Array.isArray(o.options) ? o.options : [])
        .map((x, j): QOption | null => {
          if (typeof x === "string") return { id: x, label: x };
          const xo = obj(x);
          if (!xo) return null;
          const id = str(xo.id) ?? str(xo.value) ?? `o${j}`;
          return { id, label: str(xo.label) ?? str(xo.title) ?? id, imageUrl: str(xo.imageUrl) ?? str(xo.image) ?? str(xo.thumbnailUrl) ?? str(xo.url), description: str(xo.description) };
        })
        .filter((x): x is QOption => !!x);
      const kind = str(o.kind) ?? str(o.type) ?? (options.length ? "single" : "text");
      const upload = kind === "upload";
      const accept = [...new Set((Array.isArray(o.accept) ? o.accept : ["image", "video"]).map((x) => (typeof x === "string" ? (x.startsWith("video") ? "video" : x.startsWith("image") ? "image" : null) : null)))].filter(
        (x): x is "image" | "video" => x === "image" || x === "video",
      );
      const minFiles = typeof o.minFiles === "number" && o.minFiles > 0 ? o.minFiles : 1;
      const maxFiles = typeof o.maxFiles === "number" && o.maxFiles >= minFiles ? o.maxFiles : Math.max(4, minFiles);
      return {
        id: str(o.id) ?? `q${i}`,
        prompt: str(o.prompt) ?? str(o.question) ?? str(o.label) ?? "",
        options,
        allowOther: o.allowOther === true,
        multi: kind === "multi" || o.multiple === true || o.multiSelect === true,
        image: !upload && (kind === "image" || options.some((x) => !!x.imageUrl)),
        text: !upload && (kind === "text" || options.length === 0),
        upload,
        accept: accept.length ? accept : ["image", "video"],
        minFiles,
        maxFiles,
      };
    })
    .filter((q): q is QItem => !!q);
  return { title: str(d.title), questions };
}

interface UploadDraft {
  key: string;
  name: string;
  preview: string | null;
  mediaType: "image" | "video";
  status: "uploading" | "ready" | "error";
  progress?: number;
  error?: string;
  file?: AnswerFile;
}

interface Draft {
  optionIds: string[];
  text: string;
  files: UploadDraft[];
  skipped?: boolean;
}

const EMPTY_DRAFT: Draft = { optionIds: [], text: "", files: [] };
let upSeq = 0;

export type QuestionStatus = "open" | "answered" | "cancelled" | "closed";

function isAnswered(q: QItem, x: Draft | undefined): boolean {
  if (!x) return false;
  if (x.skipped) return true;
  if (q.upload) return x.files.filter((f) => f.status === "ready").length >= q.minFiles && !x.files.some((f) => f.status === "uploading");
  return x.optionIds.length > 0 || x.text.trim().length > 0;
}

export function QuestionCard({
  data,
  status,
  compact,
  brandId,
  onSubmit,
}: {
  data: Record<string, unknown> | undefined;
  status: QuestionStatus;
  compact?: boolean;
  brandId?: string | null;
  onSubmit: (answers: AnswerItem[], summary: Record<string, unknown>, cancelled: boolean) => Promise<boolean>;
}) {
  const { title, questions } = useMemo(() => normalizeQuestions(data), [data]);
  const [step, setStep] = useState(0);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [collapsed, setCollapsed] = useState(false);
  const [busy, setBusy] = useState<null | "submit" | "cancel">(null);
  const [localStatus, setLocalStatus] = useState<QuestionStatus | null>(null);
  const effective = localStatus ?? status;

  if (!questions.length) return null;
  if (effective !== "open") return <ClosedQuestion title={title} questions={questions} status={effective} />;

  const q = questions[Math.min(step, questions.length - 1)]!;
  const d = drafts[q.id] ?? EMPTY_DRAFT;
  const answered = (qq: QItem) => isAnswered(qq, drafts[qq.id]);
  const last = step >= questions.length - 1;
  const single = questions.length === 1;
  const setDraftFor = (qid: string, fn: (x: Draft) => Draft) => setDrafts((all) => ({ ...all, [qid]: fn(all[qid] ?? EMPTY_DRAFT) }));
  const setDraft = (patch: Partial<Draft>) => setDraftFor(q.id, (x) => ({ ...x, ...patch, skipped: false }));
  const toggle = (id: string) => {
    if (q.multi) setDraft({ optionIds: d.optionIds.includes(id) ? d.optionIds.filter((x) => x !== id) : [...d.optionIds, id] });
    else setDraft({ optionIds: d.optionIds[0] === id ? [] : [id], text: "" });
  };

  const submit = async (cancelled: boolean, all: Record<string, Draft> = drafts) => {
    const done = questions.filter((qq) => isAnswered(qq, all[qq.id]));
    const items: AnswerItem[] = done.map((qq) => {
      const x = all[qq.id]!;
      if (x.skipped) return { questionId: qq.id, skipped: true };
      if (qq.upload) return { questionId: qq.id, files: x.files.filter((f) => f.status === "ready" && f.file).map((f) => f.file!) };
      return { questionId: qq.id, ...(x.optionIds.length ? { optionIds: x.optionIds } : {}), ...(x.text.trim() ? { text: x.text.trim() } : {}) };
    });
    const summary = {
      cancelled,
      items: cancelled
        ? []
        : items.map((a) => {
            const qq = questions.find((x) => x.id === a.questionId)!;
            const x = all[qq.id]!;
            return {
              questionId: a.questionId,
              prompt: qq.prompt,
              selected: (a.optionIds ?? []).map((id) => qq.options.find((o) => o.id === id)).filter(Boolean),
              ...(a.text ? { text: a.text } : {}),
              ...(a.skipped ? { skipped: true } : {}),
              ...(qq.upload && !a.skipped ? { files: x.files.filter((f) => f.status === "ready").map((f) => ({ name: f.name, url: f.preview, mediaType: f.mediaType })) } : {}),
            };
          }),
    };
    setBusy(cancelled ? "cancel" : "submit");
    const ok = await onSubmit(cancelled ? [] : items, summary, cancelled);
    setBusy(null);
    if (ok) setLocalStatus(cancelled ? "cancelled" : "answered");
  };

  const advance = (all?: Record<string, Draft>) => {
    if (last) void submit(false, all);
    else setStep((x) => x + 1);
  };

  return (
    <section className="my-3 animate-rise overflow-hidden rounded-2xl border border-line bg-[#1f1f21] shadow-[0_10px_40px_-20px_rgba(0,0,0,0.7)]" aria-label={title ?? "Questions from Mimic"}>
      <div className={cn("flex items-center justify-between gap-3", compact ? "px-3.5 pt-3.5" : "px-5 pt-4", (collapsed || (q.upload && single)) && (compact ? "pb-3" : "pb-4"), q.upload && single && !collapsed && "border-b border-line")}>
        {single ? (
          <h3 className={cn("min-w-0 font-semibold leading-snug", compact ? "text-[15px]" : "text-[17px]")}>{q.prompt || title || "Mimic has a question"}</h3>
        ) : (
          <div className="flex min-w-0 items-center gap-1">
            <button type="button" onClick={() => setStep((x) => Math.max(0, x - 1))} disabled={step === 0} aria-label="Previous question" className="grid size-8 place-items-center rounded-lg text-fg/80 hover:bg-hover disabled:opacity-30">
              <ChevronLeft className="size-4.5" />
            </button>
            <span className={cn("font-medium tnum", compact ? "text-[15px]" : "text-[17px]")}>
              {step + 1} of {questions.length}
            </span>
            <button
              type="button"
              onClick={() => setStep((x) => Math.min(questions.length - 1, x + 1))}
              disabled={last || !answered(q)}
              aria-label="Next question"
              className="grid size-8 place-items-center rounded-lg text-fg/80 hover:bg-hover disabled:opacity-30"
            >
              <ChevronRight className="size-4.5" />
            </button>
            {collapsed && <span className="ml-2 truncate text-[13px] text-muted">{q.prompt}</span>}
          </div>
        )}
        <button type="button" onClick={() => setCollapsed((c) => !c)} aria-label={collapsed ? "Expand questions" : "Collapse questions"} aria-expanded={!collapsed} className="grid size-8 shrink-0 place-items-center rounded-lg text-fg/80 hover:bg-hover">
          <ChevronDown className={cn("size-4.5 transition-transform", collapsed && "-rotate-90")} />
        </button>
      </div>

      {!collapsed && (
        <div key={q.id} className={cn("animate-fade-in", compact ? "px-3.5 pb-3.5" : "px-5 pb-5")}>
          {!single && (q.prompt || title) && (
            <div className="mt-2 px-1">
              {title && <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-subtle">{title}</div>}
              <div className={cn("mt-0.5 font-medium text-fg", compact ? "text-[14px]" : "text-[15px]")}>{q.prompt || title}</div>
            </div>
          )}
          {q.multi && !q.upload && <div className="mt-1 px-1 text-[12px] text-muted">Pick all that apply</div>}

          {q.upload ? (
            <UploadStep q={q} draft={d} brandId={brandId} compact={compact} onChange={(fn) => setDraftFor(q.id, (x) => ({ ...fn(x), skipped: false }))} />
          ) : q.image ? (
            <ImageCarousel options={q.options} selected={d.optionIds} onToggle={toggle} multi={q.multi} compact={compact} />
          ) : q.options.length > 0 ? (
            <TextOptions options={q.options} selected={d.optionIds} onToggle={toggle} multi={q.multi} />
          ) : null}

          {!q.upload && (q.allowOther || q.text) && (
            <div className="mt-4">
              {q.text && !q.options.length ? (
                <textarea
                  value={d.text}
                  onChange={(e) => setDraft({ text: e.target.value })}
                  rows={3}
                  placeholder="Type your answer…"
                  aria-label={q.prompt || "Your answer"}
                  className="w-full resize-y rounded-xl border border-line-strong bg-transparent px-4 py-3 text-[15px] text-fg outline-none placeholder:text-subtle focus:border-primary"
                />
              ) : (
                <input
                  value={d.text}
                  onChange={(e) => setDraft({ text: e.target.value, ...(q.multi ? {} : { optionIds: e.target.value ? [] : d.optionIds }) })}
                  placeholder="Other"
                  aria-label="Other answer"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && answered(q)) {
                      e.preventDefault();
                      advance();
                    }
                  }}
                  className="h-12 w-full rounded-xl border border-line-strong bg-transparent px-4 text-[15px] text-fg outline-none placeholder:text-subtle focus:border-primary"
                />
              )}
            </div>
          )}

          <div className="mt-5 flex items-center justify-end gap-2">
            {q.upload ? (
              <button
                type="button"
                disabled={!!busy}
                onClick={() => {
                  const all = { ...drafts, [q.id]: { ...(drafts[q.id] ?? EMPTY_DRAFT), skipped: true } };
                  setDrafts(all);
                  advance(all);
                }}
                className="h-10 rounded-xl px-3 text-[15px] font-medium text-lavender transition-colors hover:bg-primary-soft disabled:opacity-50"
              >
                Skip
              </button>
            ) : (
              <button type="button" onClick={() => void submit(true)} disabled={!!busy} className="h-10 rounded-xl px-3 text-[15px] font-medium text-lavender transition-colors hover:bg-primary-soft disabled:opacity-50">
                {busy === "cancel" ? "Cancelling…" : "Cancel"}
              </button>
            )}
            <Button
              variant="primary"
              size="lg"
              className="h-10 rounded-xl px-5 disabled:bg-primary/55 disabled:text-white/55 disabled:opacity-100"
              disabled={!answered(q) || !!busy}
              loading={busy === "submit"}
              onClick={() => advance()}
            >
              {last ? "Submit" : "Next"}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

/** Upload question: "Selected" tiles + a "+" tile (Upload / Asset Library / Creations). */
function UploadStep({ q, draft, brandId, compact, onChange }: { q: QItem; draft: Draft; brandId?: string | null; compact?: boolean; onChange: (fn: (d: Draft) => Draft) => void }) {
  const menu = usePopover();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [assetOpen, setAssetOpen] = useState(false);
  const [creationsOpen, setCreationsOpen] = useState(false);
  const [zoom, setZoom] = useState<UploadDraft | null>(null);
  const room = q.maxFiles - draft.files.length;
  const acceptAttr = q.accept.map((k) => (k === "image" ? "image/png,image/jpeg,image/webp,image/gif" : "video/mp4,video/quicktime,video/webm")).join(",");
  const add = (items: UploadDraft[]) => onChange((x) => ({ ...x, files: [...x.files, ...items].slice(0, q.maxFiles) }));
  const patchFile = (key: string, patch: Partial<UploadDraft>) => onChange((x) => ({ ...x, files: x.files.map((f) => (f.key === key ? { ...f, ...patch } : f)) }));
  const dropFile = (key: string) => onChange((x) => ({ ...x, files: x.files.filter((f) => f.key !== key) }));

  const upload = async (files: File[]) => {
    for (const file of files.slice(0, Math.max(0, room))) {
      const mediaType = file.type.startsWith("video/") ? "video" : "image";
      if (!q.accept.includes(mediaType)) {
        toast({ tone: "warning", title: `${file.name} isn't accepted`, body: `This question takes ${q.accept.join(" or ")} files.` });
        continue;
      }
      const key = `up-${++upSeq}`;
      add([{ key, name: file.name, preview: URL.createObjectURL(file), mediaType, status: "uploading", progress: 0 }]);
      try {
        const up = await uploadFile(file, { brandId, purpose: "chat", onProgress: (progress) => patchFile(key, { progress }) });
        patchFile(key, { status: "ready", file: { storageKey: up.key }, mediaType: up.kind === "video" ? "video" : "image" });
      } catch (e) {
        patchFile(key, { status: "error", error: errorMessage(e) });
      }
    }
  };

  const tile = compact ? "size-[72px]" : "size-[84px]";
  return (
    <div className="mt-4 rounded-xl bg-[#262628] p-3.5">
      <div className="mb-2.5 flex items-center justify-between text-[13px]">
        <span className="font-medium text-fg/90">Selected</span>
        <span className="text-subtle tnum">
          {draft.files.length}/{q.maxFiles}
          {q.minFiles > 1 ? ` · min ${q.minFiles}` : ""}
        </span>
      </div>
      <div className="flex flex-wrap gap-2.5">
        {draft.files.map((f) => (
          <div key={f.key} className={cn("group relative overflow-hidden rounded-lg border bg-[#151517]", tile, f.status === "error" ? "border-danger/70" : "border-line")} title={f.error ?? f.name}>
            <button type="button" onClick={() => f.preview && setZoom(f)} className="size-full" aria-label={`Preview ${f.name}`}>
              {f.preview && f.mediaType === "video" && (f.preview.startsWith("blob:") || isVideoUrl(f.preview)) ? (
                <video src={safePreviewSrc(f.preview)} muted playsInline className="size-full object-cover" />
              ) : f.preview ? (
                <img src={safePreviewSrc(f.preview)} alt={f.name} className="size-full object-cover" />
              ) : (
                <span className="grid size-full place-items-center text-subtle">{f.mediaType === "video" ? <Film className="size-5" /> : <ImageIcon className="size-5" />}</span>
              )}
            </button>
            {f.status === "uploading" && (
              <span className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/55 text-[10px] text-white">
                <Spinner size={14} />
                {f.progress ? `${Math.round(f.progress * 100)}%` : null}
              </span>
            )}
            {f.status === "error" && <span className="absolute inset-x-0 bottom-0 bg-danger/85 py-0.5 text-center text-[9.5px] font-semibold text-white">Failed</span>}
            <button
              type="button"
              onClick={() => dropFile(f.key)}
              aria-label={`Remove ${f.name}`}
              className="absolute right-1 top-1 grid size-5 place-items-center rounded-full bg-black/70 text-white opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100"
            >
              <X className="size-3" />
            </button>
          </div>
        ))}
        {room > 0 && (
          <button
            type="button"
            ref={(el) => {
              menu.anchorRef.current = el;
            }}
            onClick={menu.toggle}
            aria-label="Add a file"
            aria-expanded={menu.open}
            className={cn("grid place-items-center rounded-lg border border-line-strong bg-[#3a3a3d] text-fg/90 transition-colors hover:bg-[#444448]", tile)}
          >
            <Plus className="size-6" strokeWidth={1.5} />
          </button>
        )}
      </div>
      <PopoverPanel open={menu.open} onClose={menu.close} anchorRef={menu.anchorRef} side="bottom" align="start" className="w-[230px] rounded-2xl p-1.5">
        {[
          { label: "Upload a file", icon: <Upload className="size-4.5" strokeWidth={1.75} />, run: () => fileRef.current?.click(), disabled: false },
          { label: "Asset Library", icon: <ImageIcon className="size-4.5" strokeWidth={1.75} />, run: () => setAssetOpen(true), disabled: !brandId },
          { label: "Creations", icon: <GalleryVerticalEnd className="size-4.5" strokeWidth={1.75} />, run: () => setCreationsOpen(true), disabled: !brandId },
        ].map((r) => (
          <button
            key={r.label}
            type="button"
            role="menuitem"
            disabled={r.disabled}
            onClick={() => {
              menu.close();
              r.run();
            }}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-[14px] text-fg/90 transition-colors hover:bg-hover disabled:opacity-40"
          >
            <span className="text-fg/75">{r.icon}</span>
            {r.label}
          </button>
        ))}
      </PopoverPanel>
      <input
        ref={fileRef}
        type="file"
        hidden
        multiple={q.maxFiles > 1}
        accept={acceptAttr}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = "";
          if (files.length) void upload(files);
        }}
      />
      {brandId && (
        <>
          <AssetPicker
            brandId={brandId}
            open={assetOpen}
            onClose={() => setAssetOpen(false)}
            multiple={room > 1}
            title="Asset Library"
            onPick={(picked) =>
              add(
                picked
                  .filter((p) => p.mediaType !== "font" && q.accept.includes(p.mediaType === "video" ? "video" : "image"))
                  .slice(0, room)
                  .map((p): UploadDraft => ({ key: `up-${++upSeq}`, name: p.name ?? "Brand asset", preview: p.url, mediaType: p.mediaType === "video" ? "video" : "image", status: "ready", file: { assetId: p.id } })),
              )
            }
          />
          <LibraryPicker
            brandId={brandId}
            source="creations"
            open={creationsOpen}
            onClose={() => setCreationsOpen(false)}
            max={Math.max(1, room)}
            onPick={(picked) =>
              add(picked.filter((p) => q.accept.includes(p.mediaType)).map((p): UploadDraft => ({ key: `up-${++upSeq}`, name: p.name, preview: p.url, mediaType: p.mediaType, status: "ready", file: { creativeId: p.id } })))
            }
          />
        </>
      )}
      {zoom && <Lightbox url={zoom.preview} mediaType={zoom.mediaType} caption={zoom.name} onClose={() => setZoom(null)} />}
    </div>
  );
}

function ImageCarousel({ options, selected, onToggle, multi, compact }: { options: QOption[]; selected: string[]; onToggle: (id: string) => void; multi: boolean; compact?: boolean }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [edges, setEdges] = useState({ left: false, right: false });
  const [zoom, setZoom] = useState<number | null>(null);
  const update = () => {
    const el = ref.current;
    if (!el) return;
    setEdges({ left: el.scrollLeft > 4, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4 });
  };
  useEffect(() => {
    update();
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [options.length]);
  const scrollBy = (dir: 1 | -1) => ref.current?.scrollBy({ left: dir * (ref.current.clientWidth * 0.8), behavior: "smooth" });
  const items = options.map((o) => ({ url: o.imageUrl ?? null, caption: o.label }));
  const tileH = compact ? "h-[205px]" : "h-[262px]";
  return (
    <div className="group/car relative mt-4">
      <div ref={ref} onScroll={update} className="flex snap-x gap-3 overflow-x-auto pb-1 scrollbar-none" role={multi ? "group" : "radiogroup"}>
        {options.map((o, i) => {
          const on = selected.includes(o.id);
          return (
            <div key={o.id} className={cn("group/tile flex shrink-0 snap-start flex-col", compact ? "w-[118px]" : "w-[150px]")}>
              <div className="relative">
                <button
                  type="button"
                  role={multi ? "checkbox" : "radio"}
                  aria-checked={on}
                  aria-label={o.label}
                  onClick={() => onToggle(o.id)}
                  className={cn(
                    "relative block w-full overflow-hidden rounded-xl bg-[#2c2c2f] transition-[box-shadow,transform] duration-150 active:scale-[0.99]",
                    tileH,
                    on ? "ring-2 ring-primary ring-offset-2 ring-offset-[#1f1f21]" : "hover:ring-1 hover:ring-line-strong",
                  )}
                >
                  {safeMediaHref(o.imageUrl) ? <img src={safeMediaHref(o.imageUrl)} alt="" loading="lazy" className="size-full object-contain" /> : <span className="grid size-full place-items-center px-3 text-center text-[12px] text-muted">{o.label}</span>}
                  {on && (
                    <span className="absolute left-2 top-2 grid size-6 place-items-center rounded-full bg-primary text-white shadow">
                      <Check className="size-3.5" strokeWidth={3} />
                    </span>
                  )}
                </button>
                {o.imageUrl && (
                  <button
                    type="button"
                    onClick={() => setZoom(i)}
                    aria-label={`Enlarge ${o.label}`}
                    className="absolute right-2 top-2 grid size-8 place-items-center rounded-full bg-black/65 text-white opacity-0 backdrop-blur transition-opacity group-hover/tile:opacity-100 focus:opacity-100"
                  >
                    <Maximize2 className="size-3.5" />
                  </button>
                )}
              </div>
              <div className={cn("mt-2 truncate text-[13.5px]", on ? "text-fg" : "text-fg/80")} title={o.description ?? o.label}>
                {o.label}
              </div>
            </div>
          );
        })}
      </div>
      {edges.left && (
        <button
          type="button"
          onClick={() => scrollBy(-1)}
          aria-label="Scroll left"
          className={cn("absolute left-2 grid size-10 place-items-center rounded-full bg-[#3a3a3e]/90 text-white opacity-0 shadow-lg backdrop-blur transition-opacity group-hover/car:opacity-100 focus:opacity-100", compact ? "top-[82px]" : "top-[111px]")}
        >
          <ChevronLeft className="size-5" />
        </button>
      )}
      {edges.right && (
        <button
          type="button"
          onClick={() => scrollBy(1)}
          aria-label="Scroll right"
          className={cn("absolute right-2 grid size-10 place-items-center rounded-full bg-[#3a3a3e]/90 text-white opacity-0 shadow-lg backdrop-blur transition-opacity group-hover/car:opacity-100 focus:opacity-100", compact ? "top-[82px]" : "top-[111px]")}
        >
          <ChevronRight className="size-5" />
        </button>
      )}
      {zoom !== null && <Lightbox items={items} index={zoom} onIndex={setZoom} onClose={() => setZoom(null)} />}
    </div>
  );
}

function TextOptions({ options, selected, onToggle, multi }: { options: QOption[]; selected: string[]; onToggle: (id: string) => void; multi: boolean }) {
  const rows = options.some((o) => o.description);
  if (!rows) {
    return (
      <div className="mt-4 flex flex-wrap gap-2" role={multi ? "group" : "radiogroup"}>
        {options.map((o) => {
          const on = selected.includes(o.id);
          return (
            <button
              key={o.id}
              type="button"
              role={multi ? "checkbox" : "radio"}
              aria-checked={on}
              onClick={() => onToggle(o.id)}
              className={cn("inline-flex h-10 items-center gap-2 rounded-xl border px-4 text-[14px] font-medium transition-colors", on ? "border-primary bg-primary-soft text-fg" : "border-line-strong text-fg/85 hover:border-[#4a4a50] hover:bg-hover")}
            >
              {on && <Check className="size-3.5 text-lavender" strokeWidth={3} />}
              {o.label}
            </button>
          );
        })}
      </div>
    );
  }
  return (
    <div className="mt-4 flex flex-col gap-2" role={multi ? "group" : "radiogroup"}>
      {options.map((o) => {
        const on = selected.includes(o.id);
        return (
          <button
            key={o.id}
            type="button"
            role={multi ? "checkbox" : "radio"}
            aria-checked={on}
            onClick={() => onToggle(o.id)}
            className={cn("flex w-full items-start gap-3 rounded-xl border px-3.5 py-3 text-left transition-colors", on ? "border-primary bg-primary-soft" : "border-line hover:border-line-strong hover:bg-hover")}
          >
            <span className={cn("mt-0.5 grid size-4.5 shrink-0 place-items-center border", multi ? "rounded-[5px]" : "rounded-full", on ? "border-primary bg-primary text-white" : "border-line-strong")}>
              {on && <Check className="size-3" strokeWidth={3} />}
            </span>
            <span className="min-w-0">
              <span className="block text-[14px] font-medium">{o.label}</span>
              {o.description && <span className="mt-0.5 block text-[12.5px] leading-snug text-muted">{o.description}</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function ClosedQuestion({ title, questions, status }: { title?: string; questions: QItem[]; status: QuestionStatus }) {
  const [open, setOpen] = useState(false);
  const label = status === "answered" ? "Answered" : status === "cancelled" ? "Dismissed" : "Skipped";
  return (
    <div className="my-3 rounded-xl border border-line bg-surface/60">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left">
        <CircleHelp className="size-4 shrink-0 text-muted" />
        <span className="min-w-0 flex-1 truncate text-[13px] text-fg/85">{title ?? (questions.length > 1 ? `${questions.length} questions` : questions[0]?.prompt)}</span>
        <Pill tone={status === "answered" ? "success" : "muted"} size="xs">
          {label}
        </Pill>
        <ChevronDown className={cn("size-4 shrink-0 text-muted transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <ol className="space-y-2.5 border-t border-line px-4 py-3 text-[13px]">
          {questions.map((q, i) => (
            <li key={q.id}>
              <div className="font-medium">
                {i + 1}. {q.prompt}
              </div>
              {q.options.length > 0 && <div className="mt-0.5 text-muted">{q.options.map((o) => o.label).join(" · ")}</div>}
              {q.upload && <div className="mt-0.5 text-muted">File upload ({q.accept.join(" / ")})</div>}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// ─── Answers (rendered as the user's message) ─────────────────────────────

interface AnswerRow {
  prompt: string;
  selected: { id: string; label: string; imageUrl?: string }[];
  files: { name?: string; url?: string; mediaType?: string }[];
  text?: string;
  skipped?: boolean;
}

function normalizeAnswerRows(data: Record<string, unknown> | undefined): { rows: AnswerRow[]; cancelled: boolean } {
  const d = data ?? {};
  const raw = Array.isArray(d.items) ? d.items : Array.isArray(d.answers) ? d.answers : [];
  const rows = raw
    .map((r): AnswerRow | null => {
      const o = obj(r);
      if (!o) return null;
      const selected = (Array.isArray(o.selected) ? o.selected : Array.isArray(o.options) ? o.options : [])
        .map((x) => {
          const so = obj(x);
          if (!so) return typeof x === "string" ? { id: x, label: x } : null;
          return { id: str(so.id) ?? "", label: str(so.label) ?? str(so.id) ?? "", imageUrl: str(so.imageUrl) ?? str(so.image) };
        })
        .filter((x): x is { id: string; label: string; imageUrl: string | undefined } => !!x);
      const files = (Array.isArray(o.files) ? o.files : [])
        .map((f) => obj(f))
        .filter((f): f is Record<string, unknown> => !!f)
        .map((f) => ({ name: str(f.name), url: str(f.url), mediaType: str(f.mediaType) }));
      return { prompt: str(o.prompt) ?? str(o.question) ?? str(o.questionId) ?? "", selected, files, text: str(o.text), skipped: o.skipped === true };
    })
    .filter((x): x is AnswerRow => !!x);
  return { rows, cancelled: d.cancelled === true || d.status === "cancelled" };
}

/** PlayAd answers card: bordered, question in bold, the chosen answer (thumbnail / file / text) under it. */
export function AnswersCard({ data, className }: { data: Record<string, unknown> | undefined; className?: string }) {
  const { rows, cancelled } = useMemo(() => normalizeAnswerRows(data), [data]);
  const [zoom, setZoom] = useState<{ url: string; mediaType?: string } | null>(null);
  const shell = "ml-auto w-fit max-w-[88%] rounded-2xl border border-line-strong bg-surface/40 px-5 py-3.5";
  if (cancelled || !rows.length) {
    return <div className={cn(shell, "text-[14px] text-muted", className)}>{cancelled ? "Dismissed the questions" : "Answered"}</div>;
  }
  return (
    <div className={cn(shell, className)}>
      <ul className="space-y-3">
        {rows.map((r, i) => (
          <li key={i}>
            {r.prompt && <div className="text-[15px] font-semibold leading-snug text-fg">{r.prompt}</div>}
            <div className="mt-1 flex flex-wrap items-end gap-2.5">
              {r.skipped && <span className="text-[15px] text-muted">Skipped</span>}
              {r.selected.map((sel) =>
                safeMediaHref(sel.imageUrl) ? (
                  <button key={sel.id} type="button" onClick={() => setZoom({ url: safeMediaHref(sel.imageUrl)! })} className="flex flex-col items-start gap-1 text-left" aria-label={`View ${sel.label}`}>
                    <img src={safeMediaHref(sel.imageUrl)} alt="" className="h-24 w-[54px] rounded-lg bg-[#2c2c2f] object-contain" loading="lazy" />
                    <span className="max-w-[110px] truncate text-[13px] text-fg/85">{sel.label}</span>
                  </button>
                ) : (
                  <span key={sel.id} className="text-[15px] text-fg/85">
                    {sel.label}
                  </span>
                ),
              )}
              {r.files.map((f, j) =>
                safePreviewSrc(f.url) ? (
                  <button key={j} type="button" onClick={() => setZoom({ url: safePreviewSrc(f.url)!, mediaType: f.mediaType })} className="size-16 overflow-hidden rounded-lg border border-line bg-[#151517]" aria-label={`View ${f.name ?? "file"}`}>
                    {f.mediaType === "video" ? <video src={safePreviewSrc(f.url)} muted playsInline className="size-full object-cover" /> : <img src={safePreviewSrc(f.url)} alt={f.name ?? ""} className="size-full object-cover" loading="lazy" />}
                  </button>
                ) : (
                  <span key={j} className="text-[14px] text-fg/85">
                    {f.name ?? "File"}
                  </span>
                ),
              )}
              {r.text && <span className="text-[15px] text-fg/85">{r.selected.length ? `“${r.text}”` : r.text}</span>}
            </div>
          </li>
        ))}
      </ul>
      {zoom && <Lightbox url={zoom.url} mediaType={zoom.mediaType} onClose={() => setZoom(null)} />}
    </div>
  );
}
